import { fetchMoreInventoriesListIfNeed } from "@Pages/Search/data/searchPageReducer";
import { AppState } from "@Store/index";
import throttle from "@Utils/throttle";
import { useEffect, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { removeShortlistParamFromURL } from "../SearchListItem/components/ShortListButton/helper";
import { updateSearchViewedFlag } from "@App/appReducer";
import setCookie from "helpers/setCookie";
import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { getPricingCurrency } from "../SearchListItem/helper";

/** Check if URL has ?p=N (N > 1) to differentiate paginated URL from infinite scroll */
const isPaginatedUrl = (): boolean => {
  if (typeof window === "undefined") return false;
  const searchParams = new URLSearchParams(window.location.search);
  const p = searchParams.get("p");
  return !!p && parseInt(p, 10) > 1;
};

interface UseSearchListOptions {
  scrollContainer?: HTMLElement | null;
}

const useSearchList = (options?: UseSearchListOptions) => {
  const { scrollContainer } = options || {};
  const { readyStatus, items, meta, filters, region, customLocation, whiteLabelPartnerData } =
    useSelector(
      ({ searchPageData, appData }: AppState) => ({
        readyStatus: searchPageData.searchList.readyStatus,
        items: searchPageData.searchList.items,
        meta: searchPageData.searchList.meta,
        filters: searchPageData.filters,
        region: searchPageData.region,
        canonical_url: searchPageData.canonical_url,
        isDesktop: appData.isDesktop,
        customLocation: searchPageData.customLocation || {},
        whiteLabelPartnerData: searchPageData.whiteLabelPartnerData,
      }),
      shallowEqual,
    );

  const isLoading = readyStatus === "request";
  const listingRef = useRef<any>(null);
  const dispatch = useDispatch();

  const isPaginated = isPaginatedUrl();

  const handleScroll = throttle(() => {
    if (isPaginated) return; // On paginated URL, disable infinite scroll
    if (readyStatus !== "success") return;

    if (scrollContainer) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      if (scrollTop + clientHeight >= scrollHeight - 500) {
        dispatch(fetchMoreInventoriesListIfNeed());
      }
      return;
    }

    if (listingRef.current && listingRef.current.getBoundingClientRect().bottom - 2500 < 0) {
      dispatch(fetchMoreInventoriesListIfNeed());
    }
  }, 400);

  useEffect(() => {
    removeShortlistParamFromURL();
  }, []);

  useEffect(() => {
    const scrollTarget = scrollContainer || window;
    scrollTarget.addEventListener("scroll", handleScroll);
    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [readyStatus, scrollContainer]);

  useEffect(() => {
    if (scrollContainer && readyStatus === "success") {
      handleScroll();
    }
  }, [scrollContainer, readyStatus, items.length]);

  const addSearchedLocationToLocalStorage = (place: any) => {
    try {
      if (typeof window !== "undefined" && window?.localStorage && place.id) {
        const recentlyVisited = JSON.parse(localStorage?.getItem("recentlyVisited") ?? "[]") || [];
        const searches = [...recentlyVisited];
        const index = searches?.findIndex((s) => s?.id === place?.id);
        if (index !== -1) {
          searches?.splice(index, 1);
        }

        if (searches?.length >= 8) {
          searches?.pop();
        }

        searches?.unshift(place);
        localStorage?.setItem("recentlyVisited", JSON.stringify(searches));
        setCookie({
          cname: "searchViewedFlag",
          cvalue: "true",
          exdays: 365,
        });
        dispatch(updateSearchViewedFlag(true));
      }
    } catch (err) {
      console.log(err);
    }
  };
  // Formatted currency
  const regionCurrency = getCurrencyFromRegion(region?.combined_name);
  const currency = getPricingCurrency(regionCurrency);
  useEffect(() => {
    if (readyStatus === "success") {
      addSearchedLocationToLocalStorage({
        id: region?.canonical_name,
        canonical_name: region?.canonical_name,
        name: region?.name,
        region_type: region?.region_type,
        filters,
        currency,
        name_zh: region?.name_zh ?? "",
        ...(region?.is_whitelabel_region && {
          is_whitelabel_region: region.is_whitelabel_region,
          whitelabel_partner_ids: region.whitelabel_partner_ids ?? [],
        }),
        country: region?.location?.country?.long_name ?? "",
      });
    }
  }, [readyStatus]);

  return {
    items,
    handleScroll,
    listingRef,
    isLoading,
    meta,
    region,
    filters,
    customLocation,
    whiteLabelPartnerData,
  };
};

export default useSearchList;
